import AddIcon from '@mui/icons-material/Add';
import { Backdrop, CircularProgress, Portal, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../../components/Button/Button';
import CustomDrawer from '../../components/Drawer/Drawer';
import Modal from '../../components/Modal/Modal';
import PreOrderModal, { PreOrderModalSubmitData } from '../../components/PreOrderModal/PreOrderModal';
import CustomSwitch from '../../components/Switch/Switch';
import TabHeader from '../../components/TabHeader/TabHeader';
import Toast from '../../components/Toast/Toast';
import { LANG_TEXT } from '../../constants/language';
import { useCategoriesContext } from '../../context/CategoriesContext';
import { useMenuContext } from '../../context/MenuContext';
import { usePreorderRules } from '../../context/PreorderRulesContext/PreorderRulesContext';
import { useSettingsContext } from '../../context/SettingsContext';
import usePermission from '../../hooks/usePermission';
import { usePreorderEnabled } from '../../hooks/usePreorderEnabled';
import { CustomTabProps } from '../../interfaces/index.types';
import { updateDoc, writeBatch } from '../../libs/firestore-wrapper';
import { getCurrentSettings, getFoodDetails } from '../../modals/order';
import { PreorderRuleState } from '../../pages/PreoderRules/PreorderRules.types';
import { convertAPIToPreorderRuleState, getStadiumPreorderRuleState } from '../../utils/preorderRules';
import MANAGE_CATEGORY_ICON from '../../assets/svg/manage_category_icon.svg';
import AddFood from './components/AddFood/AddFood';
import { ListMenu } from './components/ListMenu/ListMenu';
import MenuCategoryManager from './components/MenuCategoryManager/MenuCategoryManager';
import { ICategoryDetails, IFoodDetails } from './menu.types';
import { buttonAddFood, buttonManageCategory, useStyles } from './styles';

export default function MenuManagement() {
  const classes = useStyles();
  const { foodMenu, loading, setFoodMenu } = useMenuContext();
  const { refetchData: refetchCategories } = useCategoriesContext();
  const { currentSetting, setCurrentSetting, stadiumData } = useSettingsContext();
  const { enabledNewVersion } = usePreorderEnabled();
  const { currentValues: preorderRulesValues } = usePreorderRules();

  const masterPreorderRule = useMemo(
    (): PreorderRuleState => getStadiumPreorderRuleState(stadiumData, preorderRulesValues.preorderRule),
    [stadiumData, preorderRulesValues.preorderRule]
  );

  const preOrderInitialRule = useMemo((): PreorderRuleState => {
    return convertAPIToPreorderRuleState(currentSetting?.preorder_rules, masterPreorderRule);
  }, [currentSetting?.preorder_rules, masterPreorderRule]);

  const [value, setValue] = useState(0);
  const { t } = useTranslation();
  const { restaurantId } = usePermission();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [openCategoryManager, setOpenCategoryManager] = useState(false);
  const [categories, setCategories] = useState<ICategoryDetails[]>([]);
  const [openToast, setOpenToast] = useState(false);
  const [confirmModal, setConfirmModal] = useState<boolean>(false);
  const [preOrderModal, setPreOrderModal] = useState<boolean>(false);
  const [isBulkPreOrderUpdating, setIsBulkPreOrderUpdating] = useState<boolean>(false);
  const pendingAction = useRef<{ type: 'enabled' | 'enabled_pre_order'; value: boolean } | null>(null);
  const [tabs, setTabs] = useState<CustomTabProps[]>([]);
  const [editData, setEditData] = useState(null);

  const [toast, setToast] = useState<{ type: 'error' | 'success'; content: string }>({
    type: 'success',
    content: '',
  });

  const toggleDrawer = (open: boolean, data: any) => {
    setEditData(data);
    setOpenDrawer(open);
  };

  const handleBulkPreOrderChange = (isChecked: boolean) => {
    if (isBulkPreOrderUpdating) return;

    pendingAction.current = { type: 'enabled_pre_order', value: isChecked };

    if (!isChecked) {
      setConfirmModal(true);
    } else {
      setPreOrderModal(true);
    }
  };

  const handleBulkRegularOrderChange = (isChecked: boolean) => {
    pendingAction.current = { type: 'enabled', value: isChecked };

    if (!isChecked) {
      setConfirmModal(true);
    } else {
      confirmBulkAction();
    }
  };

  const confirmBulkAction = async () => {
    if (!pendingAction.current) return;

    const { type, value } = pendingAction.current;
    const isPreOrderAction = type === 'enabled_pre_order';

    if (isPreOrderAction) {
      setIsBulkPreOrderUpdating(true);
    }

    setFoodMenu((prevStates) => ({
      ...prevStates,
      foods: prevStates.foods.map((foodItem) => ({
        ...foodItem,
        [type]: value,
      })),
    }));

    try {
      const foods = foodMenu.foods;
      const BATCH_SIZE = 500; // Firestore batch limit

      for (let i = 0; i < foods.length; i += BATCH_SIZE) {
        const batch = writeBatch();
        const chunk = foods.slice(i, i + BATCH_SIZE);

        chunk.forEach((food) => {
          batch.update(getFoodDetails(food.id), { [type]: value });
        });

        await batch.commit();
      }
      try {
        await refetchCategories();
      } catch (e) {
        console.warn('Failed to refetch categories after bulk update', e);
      }
      const actionLabel = type === 'enabled' ? t(LANG_TEXT.AVAILABLE) : t(LANG_TEXT.PRE_ORDER);
      const statusText = value ? t(LANG_TEXT.ENABLED) : t(LANG_TEXT.DISABLED);
      setToast({
        type: 'success',
        content: `${actionLabel} ${statusText} ${t(LANG_TEXT.FOR_ALL_ITEMS)}`,
      });
      setOpenToast(true);
    } catch (error) {
      console.error('Batch update error:', error);
      const errorKey = type === 'enabled' ? 'BULK_ACTION_ERROR_AVAILABILITY' : 'BULK_ACTION_ERROR_PRE_ORDER';
      setToast({
        type: 'error',
        content: t(LANG_TEXT[errorKey as keyof typeof LANG_TEXT]),
      });
      setOpenToast(true);

      setFoodMenu((prevStates) => ({
        ...prevStates,
        foods: prevStates.foods.map((foodItem) => ({
          ...foodItem,
          [type]: !value,
        })),
      }));
    } finally {
      pendingAction.current = null;
      setConfirmModal(false);
      if (isPreOrderAction) {
        setIsBulkPreOrderUpdating(false);
      }
    }
  };

  const handlePreOrderModalConfirm = async (formData: PreOrderModalSubmitData) => {
    const emailsArray = formData.emails
      .split(',')
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    const { preorderRule } = formData;
    const unitToType = (unit: string) => {
      if (unit === 'minute') return 'minutes';
      if (unit === 'hour') return 'hours';
      if (unit === 'day') return 'days';
      return 'days';
    };

    const acceptance = {
      since: {
        at: preorderRule.acceptanceSince.pickerValue,
        time: preorderRule.acceptanceSince.value,
        type: unitToType(preorderRule.acceptanceSince.unit),
      },
      until: {
        at: preorderRule.acceptanceUntil.pickerValue,
        time: preorderRule.acceptanceUntil.value,
        type: unitToType(preorderRule.acceptanceUntil.unit),
      },
    };
    const orderDeliverable = {
      since: {
        time: 0,
        type: 'minutes',
      },
      until: {
        time: preorderRule.orderDeliverableUntil.value,
        type: unitToType(preorderRule.orderDeliverableUntil.unit),
      },
    };

    try {
      await updateDoc(getCurrentSettings(restaurantId), {
        'preorder_rules.pre_order_email_addresses': emailsArray,
        'preorder_rules.acceptance': acceptance,
        'preorder_rules.order_deliberable': orderDeliverable,
      });

      setCurrentSetting((prev: any) => ({
        ...prev,
        preorder_rules: {
          ...(prev.preorder_rules || {}),
          pre_order_email_addresses: emailsArray,
          acceptance,
          order_deliberable: orderDeliverable,
        },
      }));
    } catch (error) {
      console.error('Failed to update email addresses:', error);
      setToast({
        type: 'error',
        content: t(LANG_TEXT.UPDATE_EMAIL_ERROR),
      });
      setOpenToast(true);
      return; // Stop
    }

    setPreOrderModal(false);
    setIsBulkPreOrderUpdating(true);

    try {
      await confirmBulkAction();
    } catch (error) {
      console.error('Failed to execute bulk action:', error);
      setToast({
        type: 'error',
        content: t(LANG_TEXT.BULK_UPDATE_ERROR),
      });
      setOpenToast(true);
    } finally {
      setIsBulkPreOrderUpdating(false);
    }
  };

  const handleChange = (newValue: number) => {
    setValue(newValue);
  };

  const getUpdatedTabs = (updatedCategories: ICategoryDetails[]): CustomTabProps[] => {
    const allData: IFoodDetails[] = [];
    const customTabs: CustomTabProps[] = updatedCategories
      .filter((category) => category.enabled !== false)
      .map((category: ICategoryDetails, index: number) => {
        const tasks = foodMenu.foods.filter((food: IFoodDetails) => {
          if (enabledNewVersion) {
            const categories = Array.isArray(food.categories_menu_ids) ? food.categories_menu_ids.map((cat: any) => (typeof cat === 'string' ? cat : cat.id)) : [];
            return categories.includes(category.id);
          }

          const categories = Array.isArray(food.categories) ? food.categories.map((cat: any) => (typeof cat === 'string' ? cat : cat.id)) : [];
          return categories.includes(category.id);
        });
        allData.push(...tasks);
        return { value: index + 1, label: category.title, id: index + 1, total: tasks.length, data: tasks };
      })
      .filter(Boolean);

    const uniqueData = Array.isArray(foodMenu.foods) ? [...foodMenu.foods] : [];

    return [{ label: t(LANG_TEXT.ALL), id: 0, total: 0, value: value, data: uniqueData }, ...customTabs];
  };

  useEffect(() => {
    setCategories(foodMenu.categories);
    const updatedTabs = getUpdatedTabs(foodMenu.categories);
    setTabs(updatedTabs);
  }, [foodMenu]);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const editFoodId = (location.state as any)?.editFoodId;
    if (editFoodId && foodMenu?.foods) {
      const found = foodMenu.foods.find((f: IFoodDetails) => f.id === editFoodId);
      if (found) {
        toggleDrawer(true, found);
        navigate(location.pathname, { replace: true });
      }
    }
  }, [location, foodMenu]);

  const allEnabled = useMemo(() => {
    return foodMenu.foods.some((food) => food.enabled);
  }, [foodMenu.foods]);

  const allPreOrderEnabled = useMemo(() => {
    return foodMenu.foods.some((food) => food.enabled_pre_order);
  }, [foodMenu.foods]);

  return (
    <Box
      component="main"
      className={classes.Dashboard}
      sx={{
        backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900]),
      }}
    >
      <Box className={classes.Header}>
        <Typography fontSize={24} fontWeight={500} lineHeight={'32px'} color={'var(--Gray-900, #101828)'}>
          {t(LANG_TEXT.MENU)}
        </Typography>
        {enabledNewVersion && (
          <Box className={classes.Action}>
            <Box className={classes.BulkActionContainer}>
              <Typography fontSize={16} lineHeight={'24px'} fontWeight={500} color={'var(--Gray-900, #101828)'}>
                {t(LANG_TEXT.BULK_ACTIONS)}
              </Typography>
              <Typography fontSize={12} lineHeight={'18px'} fontWeight={500} color={'var(--Gray-500, #667085)'}>
                {t(LANG_TEXT.BULK_ACTIONS_DESCRIPTION)}
              </Typography>
            </Box>
            <CustomSwitch
              onChange={(isChecked: boolean) => handleBulkRegularOrderChange(isChecked)}
              isChecked={allEnabled || false}
              label={t(LANG_TEXT.AVAILABLE)}
              className={classes.Switch}
            />
            <CustomSwitch
              onChange={(isChecked: boolean) => handleBulkPreOrderChange(isChecked)}
              isChecked={allPreOrderEnabled || false}
              disabled={isBulkPreOrderUpdating}
              label={t(LANG_TEXT.PRE_ORDER)}
              className={classes.Switch}
            />
          </Box>
        )}
        <Box className={classes.Action}>
          {!enabledNewVersion && (
            <Button
              id="manage_category"
              fullWidth={false}
              title={t(LANG_TEXT.MANAGE_CATEGORY)}
              buttonStyle={buttonManageCategory}
              sxTextStyle={{ color: 'var(--Primary-700, #296C32)', fontWeight: 600, fontSize: 14, lineHeight: '20px' }}
              boxStyle={{
                width: 'max-content !important',
              }}
              onClick={() => setOpenCategoryManager(true)}
              startIcon={<img src={MANAGE_CATEGORY_ICON} height={20} width={20} alt="MANAGE_CATEGORY_ICON" />}
            />
          )}
          <Button
            id="add_new_food"
            fullWidth={false}
            title={t(LANG_TEXT.ADD_NEW_FOOD)}
            buttonStyle={buttonAddFood}
            sxTextStyle={{ color: 'var(--Base-White, #FFF)', fontWeight: 600, fontSize: 14, lineHeight: '20px' }}
            boxStyle={{
              width: 'max-content !important',
            }}
            onClick={() => toggleDrawer(!openDrawer, null)}
            startIcon={<AddIcon sx={{ color: 'rgba(255, 255, 255, 1)' }} />}
          />
        </Box>
        <CustomDrawer
          openDrawer={openDrawer}
          toggleDrawer={toggleDrawer}
          DrawerItem={<AddFood data={editData} categories={categories} toggleDrawer={toggleDrawer} setToast={setToast} setOpenToast={setOpenToast} />}
        />
        {!enabledNewVersion && (
          <MenuCategoryManager
            open={openCategoryManager}
            handleClose={() => setOpenCategoryManager(false)}
            categories={categories}
            setOpenToast={setOpenToast}
            setToast={setToast}
          />
        )}
      </Box>
      <Box className={classes.TabPanel}>
        <TabHeader tabs={tabs} handleChange={handleChange} value={value} />
        {tabs.map((tab: CustomTabProps) => (
          <Box role="tabpanel" hidden={value !== tab.id} id={`simple-tabpanel-${tab.id}`} aria-labelledby={`simple-tab-${tab.id}`} key={tab.id}>
            {value === tab.id && <ListMenu key={tab.id} index={tab.id} value={value} result={tab.data} loading={loading} toggleDrawer={toggleDrawer} bulkPreOrderEnabled={allPreOrderEnabled} />}
          </Box>
        ))}
      </Box>
      <Toast
        content={toast.content}
        variant={toast.type}
        open={openToast}
        onClose={() => {
          setOpenToast(false);
        }}
      />
      <Modal
        open={confirmModal}
        handleClose={() => {
          setConfirmModal(false);
          pendingAction.current = null;
        }}
        variant="warning"
        handleSubmit={confirmBulkAction}
        title={t(LANG_TEXT.BULK_ACTION_CONFIRMATION_TITLE)}
        description={t(LANG_TEXT.BULK_ACTION_CONFIRMATION_DESCRIPTION)}
      />
      <PreOrderModal
        open={preOrderModal}
        handleClose={() => {
          setPreOrderModal(false);
          pendingAction.current = null;
        }}
        handleSubmit={handlePreOrderModalConfirm}
        initialRule={preOrderInitialRule}
        initialEmails={(currentSetting?.preorder_rules?.pre_order_email_addresses || []).join(', ')}
        masterRule={masterPreorderRule}
      />
      <Portal>
        <Backdrop
          open={isBulkPreOrderUpdating}
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.modal + 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <CircularProgress color="inherit" />
          <Typography fontSize={16} fontWeight={600}>
            {t(LANG_TEXT.SAVING)}
          </Typography>
        </Backdrop>
      </Portal>
    </Box>
  );
}
